/**
 * Form controls at admin density.
 *
 * `onChange` hands back the value, not the event. The design's kit calls these as
 * `onChange={setQ}`, and threading events through 42 screens buys nothing.
 *
 * Two rules carried over from the design system:
 *   - `borderStrong` on every input. Cream-on-cream needs a real edge or the field disappears.
 *   - Disabled is the `sunk` fill with `ink3` content — never opacity.
 */
import { useId, type CSSProperties } from 'react';
import { Icon } from './primitives';
import type { IconName } from './icons';
import { parsePaise } from '../lib/money';

export type Density = 'mobile' | 'admin';

const FIELD_HEIGHT: Record<Density, string> = {
  admin: 'var(--input-admin)',
  mobile: 'var(--tt-min)',
};

function fieldSkin(disabled: boolean, invalid: boolean): CSSProperties {
  return {
    background: disabled ? 'var(--c-sunk)' : 'var(--c-surface2)',
    color: disabled ? 'var(--c-ink3)' : 'var(--c-ink)',
    borderColor: invalid ? 'var(--c-error)' : 'var(--c-border-strong)',
  };
}

/* ────────────────────────────  Input  ──────────────────────────── */

export interface InputProps {
  label?: string;
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  /** Rendered inside the field, before the text — `₹` on money fields. */
  prefix?: string;
  icon?: IconName;
  helper?: string;
  /** A string turns the field red and replaces the helper. */
  error?: string;
  disabled?: boolean;
  multiline?: boolean;
  inputMode?: 'text' | 'numeric' | 'tel' | 'decimal';
  type?: 'text' | 'password' | 'date';
  density?: Density;
  id?: string;
  style?: CSSProperties;
}

export function Input({
  label,
  value,
  onChange,
  placeholder,
  prefix,
  icon,
  helper,
  error,
  disabled = false,
  multiline = false,
  inputMode,
  type = 'text',
  density = 'admin',
  id,
  style,
}: InputProps) {
  const autoId = useId();
  const fieldId = id ?? autoId;
  const describedBy = error || helper ? `${fieldId}-note` : undefined;
  const invalid = Boolean(error);

  const shared = {
    id: fieldId,
    value: value ?? '',
    placeholder,
    disabled,
    'aria-invalid': invalid || undefined,
    'aria-describedby': describedBy,
    onChange: (e: { target: { value: string } }) => onChange?.(e.target.value),
    style: {
      flex: 1,
      minWidth: 0,
      border: 'none',
      outline: 'none',
      background: 'transparent',
      font: 'inherit',
      fontFamily: 'var(--font-body)',
      fontSize: 'var(--fs-admin-ui)',
      color: 'inherit',
      resize: multiline ? ('vertical' as const) : undefined,
      padding: multiline ? 'var(--sp-sm) 0' : 0,
    },
  };

  return (
    <div style={{ display: 'grid', gap: 4, minWidth: 0, ...style }}>
      {label && (
        <label htmlFor={fieldId} style={{ fontSize: 'var(--fs-admin-cell)', color: 'var(--c-ink2)' }}>
          {label}
        </label>
      )}
      <div
        style={{
          display: 'flex',
          alignItems: multiline ? 'flex-start' : 'center',
          gap: 'var(--sp-sm)',
          minHeight: multiline ? undefined : FIELD_HEIGHT[density],
          padding: '0 var(--sp-md)',
          border: '1px solid',
          borderRadius: 'var(--r-input)',
          ...fieldSkin(disabled, invalid),
        }}
      >
        {icon && <Icon name={icon} size={14} style={{ color: 'var(--c-ink3)' }} />}
        {prefix && (
          <span className="mono-num" style={{ color: 'var(--c-ink3)', fontSize: 'var(--fs-admin-ui)' }}>
            {prefix}
          </span>
        )}
        {multiline ? <textarea rows={3} {...shared} /> : <input type={type} inputMode={inputMode} {...shared} />}
      </div>
      {(error || helper) && (
        <span
          id={describedBy}
          style={{
            fontSize: 'var(--fs-admin-colhead)',
            color: invalid ? 'var(--c-error-text)' : 'var(--c-ink3)',
          }}
        >
          {error ?? helper}
        </span>
      )}
    </div>
  );
}

/* ────────────────────────────  Select  ──────────────────────────── */

export interface SelectProps {
  label?: string;
  value?: string;
  onChange?: (value: string) => void;
  options: readonly string[];
  placeholder?: string;
  disabled?: boolean;
  density?: Density;
  id?: string;
  style?: CSSProperties;
  /** The accessible name when there is no visible `label`; falls back to the placeholder. */
  ariaLabel?: string;
  /** What an option reads as, when its value is a code (`late_capture_slot_lost`). */
  optionLabel?: (value: string) => string;
}

export function Select({
  label,
  value,
  onChange,
  options,
  placeholder,
  disabled = false,
  density = 'admin',
  id,
  style,
  ariaLabel,
  optionLabel,
}: SelectProps) {
  const autoId = useId();
  const fieldId = id ?? autoId;
  return (
    <div style={{ display: 'grid', gap: 4, minWidth: 0, ...style }}>
      {label && (
        <label htmlFor={fieldId} style={{ fontSize: 'var(--fs-admin-cell)', color: 'var(--c-ink2)' }}>
          {label}
        </label>
      )}
      <select
        id={fieldId}
        aria-label={label ? undefined : (ariaLabel ?? placeholder)}
        value={value ?? ''}
        disabled={disabled}
        onChange={(e) => onChange?.(e.target.value)}
        style={{
          height: FIELD_HEIGHT[density],
          padding: '0 var(--sp-md)',
          border: '1px solid',
          borderRadius: 'var(--r-input)',
          fontFamily: 'var(--font-body)',
          fontSize: 'var(--fs-admin-ui)',
          cursor: disabled ? 'not-allowed' : 'pointer',
          ...fieldSkin(disabled, false),
        }}
      >
        {placeholder !== undefined && <option value="">{placeholder}</option>}
        {/* A value that names no option would leave the browser painting the FIRST option as
            though it were chosen while the state says nothing is — the live related-puja picker
            showed "Satyanarayan Puja" over a disabled Add button. Say so instead. */}
        {placeholder === undefined && !options.includes(value ?? '') && (
          <option value="" disabled>
            Choose…
          </option>
        )}
        {options.map((o) => (
          <option key={o} value={o}>
            {o === '' ? (placeholder ?? '—') : (optionLabel?.(o) ?? o)}
          </option>
        ))}
      </select>
    </div>
  );
}

/* ────────────────────────────  Toggle  ──────────────────────────── */

export interface ToggleProps {
  on: boolean;
  /** A locked toggle states WHY it cannot move, rather than looking merely broken. */
  locked?: boolean;
  lockedReason?: string;
  onChange?: (next: boolean) => void;
  label: string;
  style?: CSSProperties;
}

export function Toggle({ on, locked = false, lockedReason, onChange, label, style }: ToggleProps) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label={label}
      disabled={locked}
      data-tip={locked ? (lockedReason ?? 'Always on — this cannot be silenced') : undefined}
      onClick={() => onChange?.(!on)}
      style={{
        width: 44,
        height: 24,
        flex: '0 0 auto',
        position: 'relative',
        padding: 0,
        borderRadius: 'var(--r-pill)',
        border: `1px solid ${on ? 'var(--c-saffron)' : 'var(--c-border-strong)'}`,
        background: locked ? 'var(--c-sunk)' : on ? 'var(--c-saffron)' : 'var(--c-surface2)',
        cursor: locked ? 'not-allowed' : 'pointer',
        ...style,
      }}
    >
      <span
        style={{
          position: 'absolute',
          top: 2,
          left: on ? 22 : 2,
          width: 18,
          height: 18,
          borderRadius: 'var(--r-pill)',
          background: locked ? 'var(--c-ink3)' : on ? 'var(--c-surface2)' : 'var(--c-ink3)',
        }}
      />
    </button>
  );
}

export interface MoneyFieldProps {
  label: string;
  /** The text as typed: the field keeps it, so "25." mid-edit is not rewritten under the cursor. */
  value: string;
  /** The text, and what it means in integer paise — null when empty or not a rupee amount. */
  onChange: (value: string, paise: number | null) => void;
  /** Refuse zero and negatives (a refund, a budget); a correction may need a signed amount. */
  positive?: boolean;
  /** A caller's own refusal (over a cap, say) wins over the field's. */
  error?: string;
  helper?: string;
  density?: Density;
  style?: CSSProperties;
}

/**
 * The rupee input (doc 03 §6.6): ₹ display bound to integer paise through `parsePaise`, which reads
 * digits, never `parseFloat`. The field says what is wrong with what was typed; whether an empty
 * field is allowed is the caller's call.
 */
export function MoneyField({ label, value, onChange, positive = false, error, helper, density, style }: MoneyFieldProps) {
  const paise = parsePaise(value);
  const own =
    value.trim() === ''
      ? undefined
      : paise === null
        ? 'Rupees, like 25 or 25.50'
        : positive && paise <= 0
          ? 'A positive rupee amount'
          : undefined;
  return (
    <Input
      label={label}
      prefix="₹"
      inputMode="decimal"
      value={value}
      onChange={(next) => onChange(next, parsePaise(next))}
      error={error ?? own}
      helper={helper}
      density={density}
      style={style}
    />
  );
}

interface FileFieldProps {
  /** The visible label beside the picker. */
  label: string;
  /** The picker's accessible name — what the file is for, e.g. "Signed agreement for …". */
  inputLabel: string;
  accept?: string;
  disabled?: boolean;
  /** The upload is under way: say so rather than leave the picker looking idle. */
  pending?: boolean;
  /** Reading this file back is an audited act; say so before anyone uploads it. */
  audited?: boolean;
  onFile: (file: File) => void;
  style?: CSSProperties;
}

/**
 * The picker half of the private-media pattern (doc 03 §6.14): choose → presign → PUT
 * (`putPresigned`) → confirm, the last three in the caller's mutation. The input is cleared after
 * each pick, so choosing the same file again after a refusal tries again instead of doing nothing.
 */
export function FileField({ label, inputLabel, accept, disabled, pending = false, audited = false, onFile, style }: FileFieldProps) {
  return (
    <label style={{ fontSize: 'var(--fs-admin-colhead)', color: 'var(--c-ink2)', display: 'grid', gap: 'var(--sp-xs)', ...style }}>
      {label}
      <input
        type="file"
        aria-label={inputLabel}
        accept={accept}
        disabled={disabled}
        onChange={(e) => {
          const file = e.target.files?.[0];
          e.target.value = '';
          if (file) onFile(file);
        }}
      />
      {pending && <span role="status">Uploading…</span>}
      {audited && <span style={{ color: 'var(--c-ink3)' }}>Access to this file is logged.</span>}
    </label>
  );
}
